import { useState, useEffect, useCallback } from 'react';
import { fetchJobData, clearJobCache } from '../utils/jobSheetsData';

const REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes

// Fallback dummy data in case Google Sheets fetch fails
const fallbackJobs = [
  {
    id: 1,
    titleTr: 'Yazılım Mühendisi',
    titleEn: 'Software Engineer',
    company: 'TechCorp Ltd.',
    city: 'Londra',
    cityEn: 'London',
    country: 'Birleşik Krallık',
    countryEn: 'United Kingdom',
    link: '#'
  },
  {
    id: 2,
    titleTr: 'Pazarlama Müdürü',
    titleEn: 'Marketing Manager',
    company: 'BrandWorks Agency',
    city: 'Amsterdam',
    cityEn: 'Amsterdam',
    country: 'Hollanda',
    countryEn: 'Netherlands',
    link: '#'
  },
  {
    id: 3,
    titleTr: 'Finans Analisti',
    titleEn: 'Financial Analyst',
    company: 'FinanceHub',
    city: 'Frankfurt',
    cityEn: 'Frankfurt',
    country: 'Almanya',
    countryEn: 'Germany',
    link: '#'
  }
];

/**
 * Custom hook to fetch job listings from Google Sheets
 * Falls back to static data if fetch fails
 *
 * @param {boolean} useGoogleSheets - Whether to fetch from Google Sheets
 * @returns {{ jobs: Array, loading: boolean, error: Error|null }}
 */
export function useJobData(useGoogleSheets = true) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (isRefresh = false) => {
    // Only show loading spinner on initial load, not on background refreshes
    if (!isRefresh) {
      setLoading(true);
    }
    setError(null);

    try {
      if (useGoogleSheets) {
        if (isRefresh) {
          clearJobCache(); // Force fresh fetch on refresh
        }
        const data = await fetchJobData();
        setJobs(data);
      } else {
        setJobs(fallbackJobs);
      }
    } catch (err) {
      console.error('Error loading job data:', err);
      setError(err);
      // Fallback to static data on error
      setJobs(fallbackJobs);
    } finally {
      setLoading(false);
    }
  }, [useGoogleSheets]);

  useEffect(() => {
    fetchData(false);

    // Auto-refresh every 5 minutes
    if (useGoogleSheets) {
      const interval = setInterval(() => fetchData(true), REFRESH_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [fetchData, useGoogleSheets]);

  return { jobs, loading, error };
}

export default useJobData;
